import React, { useCallback, useEffect, useState } from 'react';
import { useDisclosure, useToast } from '@withjoy/joykit';
import { gql } from '@apollo/client';
import { ExternalProductOffersGrid } from './components/ExternalProductOffersGrid';
import { AddRegistryItemPayloadBySource, AddUniversalSearchItemToRegistryPayload, RegistryItemSource, UseSharedCatalogMutationReturn } from '../../Catalog.controller';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { PagesForTelemetry } from '../../Catalog.telemetry.types';
import { ProductUniversalItemSource, useProductExternalSearchLazyQuery, ProductExternalByIdQuery, ProductExternalByIdQueryVariables } from '@graphql/generated';
import { ExternalProductTile, ExternalProductOfferTileProps, OnProductOfferClickArgs } from './components/ExternalProductTile';
import { ExternalProduct, ExternalProductOffer } from '../../Catalog.types';
import { ExternalProductPdpModal, ExternalProductPdpModalForm } from './components/ExternalProductOfferPdpModal';
import { useTranslation } from '@shared/core';
import { useApolloClient } from '@apollo/client';
import { PRODUCT_EXTERNAL_BY_ID } from '@graphql/queries/catalog';
import { PRODUCT_EXTERNAL_SUMMARY } from '@graphql/fragments/catalog';
import { SelectedExternalProductOffer, useExternalProductReducer, ACTIONS } from './components/ExternalProductReducer';

type ExternalProductSearchProps = {
  /**
   * Intended to only be used for telemetry
   *
   * Total number of products from joy catalog search.
   */
  joyCatalogSearchResultCount: number;

  /**
   * Intended to only be used for telemetry
   *
   * How many products of the joy catalog search are currently visible
   *
   * eg. 737 total search results, but currently only 48 are shown
   */
  visibleJoyCatalogSearchResultsCount: number;
  searchQuery: string;
  onAddProductToRegistry: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  verifyUserAccessAndInvokeCb: (cb: () => void) => void;

  /**
   * 1. Prinmary: When there are search results, the universal search results grid will be placed below
   * 2. Secondary: When there are no search results, we only show the universal search results
   */
  placement: 'primary' | 'secondary';
  telemetryPageValue?: PagesForTelemetry;
  isMobile: boolean;
};

type ExternalProductPdpModalSelectionProps = {
  handleProductClick?: (args: OnProductOfferClickArgs) => void;
  handleProductAdd?: (args: HandleProductAddArgs) => void;
};

type ExternalProductOffersState = {
  loading: boolean;
  offers?: ExternalProductOffer[] | null;
};

type HandleProductAddArgs = {
  selectedOffer: SelectedExternalProductOffer;
  product?: ExternalProduct;
  addProductForm: Partial<ExternalProductPdpModalForm>;
};

type SelectedProductToRegistryPayloadArgs = HandleProductAddArgs & {
  component: AddUniversalSearchItemToRegistryPayload['component'];
  page?: PagesForTelemetry;
  searchQuery: string;
};

export const selectedProductToRegistryPayload = (args: SelectedProductToRegistryPayloadArgs): AddRegistryItemPayloadBySource => {
  const {
    selectedOffer: { offer, itemPosition },
    product,
    addProductForm,
  } = args;
  const seller = product?.sellers.find(seller => seller.name === offer.sellerName);
  const priceAsString = offer.sellerSalePrice?.floatingPointDecimalString ?? offer.sellerFullPrice.floatingPointDecimalString;
  const productUrl = offer.sellerUrl ?? seller?.url;
  if (!productUrl) {
    throw new Error('Cannot find the product URL in offer or product');
  }
  return {
    source: 'universalSearch',
    payload: {
      component: args.component,
      page: args.page,
      itemPosition,
      searchTerm: args.searchQuery,
      productUrl,
      productImageUrl: offer.productThumbnail.url,
      productTitle: offer.productTitle,
      shopName: offer.sellerName,
      productSource: ProductUniversalItemSource.googleShoppingRegular,
      qty: addProductForm.quantity ?? 1,
      title: addProductForm.title ?? offer.productTitle,
      note: addProductForm.note ?? '',
      priceAsFloatingPointDecimalString: priceAsString,
      metadata: {
        source: RegistryItemSource.googleShoppingRegular,
        sourceMetadata: {
          scannedPrice: priceAsString,
        },
      },
    },
  };
};

export const useExternalProductsWithModal = ({ handleProductClick, handleProductAdd }: ExternalProductPdpModalSelectionProps) => {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { toast } = useToast();
  const { t2: translations } = useTranslation('catalogRegistry');
  const universalSearchTrans = translations('universalSearch');
  const [state, dispatch] = useExternalProductReducer();
  const toastGetProductError = useCallback(() => toast(universalSearchTrans.getProductByIdError), [toast, universalSearchTrans]);
  const toastAddProductError = useCallback(() => toast(universalSearchTrans.addProductToRegistryError), [toast, universalSearchTrans]);
  // It was difficult to handle errors and results only once using useLazyQuery because the state from this hook remained the same all the time.
  // To simplify things, below we use the direct Apollo client. By using the Apollo client we can be sure that the error and result are handled only once.
  const apolloClient = useApolloClient();
  const loadProduct = useCallback(
    async (productId: string) => {
      try {
        dispatch({ type: ACTIONS.SET_EXTERNAL_PRODUCT_LOADING });
        const {
          data: { productExternalById: loadedProduct },
        } = await apolloClient.query<ProductExternalByIdQuery, ProductExternalByIdQueryVariables>({
          query: gql`
            ${PRODUCT_EXTERNAL_BY_ID}
            ${PRODUCT_EXTERNAL_SUMMARY}
          `,
          fetchPolicy: 'cache-first',
          variables: { productId },
        });
        dispatch({ type: ACTIONS.SET_EXTERNAL_PRODUCT_LOADED, payload: { loadedProduct } });
      } catch (productLoadingError) {
        console.error(productLoadingError);
        onClose();
        toastGetProductError();
        dispatch({ type: ACTIONS.SET_EXTERNAL_PRODUCT_LOADING_ERROR, payload: { productLoadingError } });
      }
    },
    [apolloClient, dispatch, toastGetProductError, onClose],
  );

  useEffect(() => {
    if (!state.selectedOffer || state.isProductLoading || state.productLoadingError) {
      return;
    }
    if (state.isProductRequired && !state.loadedProduct) {
      loadProduct(state.selectedOffer.offer.productId);
    }
  }, [state.selectedOffer, state.isProductLoading, state.productLoadingError, state.isProductRequired, state.loadedProduct, loadProduct]);

  useEffect(() => {
    if (!state.selectedOffer) {
      return;
    }
    if (state.addOfferWhenReady && state.isOfferReadyToAdd) {
      try {
        handleProductAdd?.({ selectedOffer: state.selectedOffer, product: state.loadedProduct, addProductForm: state.addProductForm ?? {} });
      } catch (error) {
        console.error(error);
        toastAddProductError();
      }
      onClose();
      dispatch({ type: ACTIONS.FINISH_ADDING_EXTERNAL_OFFER });
    }
  }, [state.selectedOffer, state.loadedProduct, state.addProductForm, state.addOfferWhenReady, state.isOfferReadyToAdd, toastAddProductError, handleProductAdd, dispatch, onClose]);

  const renderExternalProduct = (props: ExternalProductOfferTileProps) => {
    return (
      <ExternalProductTile
        index={props.index}
        offer={props.offer}
        isMobile={props.isMobile}
        addItemLoading={state.isProductLoading}
        onProductClick={args => {
          dispatch({ type: ACTIONS.SET_SELECTED_EXTERNAL_OFFER, payload: { selectedOffer: args } });
          onOpen();
          handleProductClick?.(args);
        }}
        onQuickAddClick={args => {
          dispatch({ type: ACTIONS.SET_SELECTED_EXTERNAL_OFFER, payload: { selectedOffer: args } });
          dispatch({ type: ACTIONS.ADD_EXTERNAL_OFFER_WHEN_READY, payload: { addProductForm: { quantity: args.quantity } } });
        }}
      />
    );
  };

  const renderExternalProductModal = () => {
    return (
      state.selectedOffer && (
        <ExternalProductPdpModal
          isOpen={isOpen}
          onClose={() => {
            onClose();
          }}
          onSubmitForm={form => {
            dispatch({ type: ACTIONS.ADD_EXTERNAL_OFFER_WHEN_READY, payload: { addProductForm: form } });
          }}
          isProductLoading={state.isProductLoading}
          offer={state.selectedOffer.offer}
          product={state.loadedProduct}
        />
      )
    );
  };
  return {
    renderExternalProductModal,
    renderExternalProduct,
  };
};

const Root = (props: ExternalProductSearchProps) => {
  const { joyCatalogSearchResultCount, onAddProductToRegistry, placement, searchQuery, telemetryPageValue, visibleJoyCatalogSearchResultsCount, isMobile } = props;
  const [{ offers, loading }, setState] = useState<ExternalProductOffersState>({ loading: false, offers: null });
  const { trackUniversalSearchResultClickAction, trackUniversalSearchInitiated } = useCatalogLoggingContext();
  const [fetchOffers, { data: result }] = useProductExternalSearchLazyQuery();

  const trackSearchAttempt = useEventCallback(() => {
    trackUniversalSearchInitiated({
      joyCatalogSearchResultsCount: joyCatalogSearchResultCount,
      visibleJoyCatalogSearchResultsCount,
      searchTermOverride: searchQuery,
      page: telemetryPageValue,
    });
  });

  useEffect(() => {
    if (searchQuery) {
      setState({ loading: true, offers: null });
      trackSearchAttempt();
      fetchOffers({ variables: { query: { text: searchQuery } } });
    } else {
      // There is nothing to query for, so reset the state.
      setState({ offers: null, loading: false });
    }
  }, [trackSearchAttempt, searchQuery, fetchOffers]);

  useEffect(() => {
    if (result?.productExternalSearch)
      setState({
        loading: false,
        offers: result.productExternalSearch.offers,
      });
  }, [result]);

  const handleProductClick = useCallback(
    ({ itemPosition, offer }: OnProductOfferClickArgs) => {
      trackUniversalSearchResultClickAction({
        label: 'grid',
        component: 'universalSearch',
        itemPosition,
        searchResult: offer,
        gridPlacement: placement,
        joyCatalogSearchResultCount,
        visibleJoyCatalogSearchResultsCount,
        searchTermOverride: searchQuery,
        page: telemetryPageValue,
      });
    },
    [joyCatalogSearchResultCount, placement, searchQuery, telemetryPageValue, trackUniversalSearchResultClickAction, visibleJoyCatalogSearchResultsCount],
  );

  const handleProductAdd = useEventCallback((args: HandleProductAddArgs) => {
    const payload = selectedProductToRegistryPayload({ ...args, page: telemetryPageValue, searchQuery, component: 'universalSearch' });
    onAddProductToRegistry(payload);
  });

  const { renderExternalProduct, renderExternalProductModal } = useExternalProductsWithModal({
    handleProductClick,
    handleProductAdd,
  });

  return (
    <>
      <ExternalProductOffersGrid
        isMobile={isMobile}
        offers={offers ?? null}
        loading={loading}
        renderOffer={renderExternalProduct}
        hideHeader={placement === 'primary'}
      />
      {renderExternalProductModal()}
    </>
  );
};

const Wrapper = withLazyPropOnIntersection(
  React.forwardRef<HTMLDivElement, ExternalProductSearchProps & { isOnScreen: boolean }>((props, ref) => {
    return (
      <div
        data-id="usearch"
        ref={ref}
      >
        {props.isOnScreen ? <Root {...props} /> : null}
      </div>
    );
  }),
  'isOnScreen',
  1250,
);

export const UniversalProductSearch = (props: ExternalProductSearchProps) => {
  return (
    <Wrapper
      {...props}
      isOnScreen={{
        onscreen: true,
        offscreen: false,
      }}
    />
  );
};
